import { solveOpacity } from '@/lib/public/effects/strength';
import type { WatermarkPlacement } from '@/lib/public/effects/catalogue';

/**
 * The "Logo watermark" background effect: the organiser's logo drawn large and
 * faint behind the public event page.
 *
 * The logo is reduced to a single-colour silhouette (so a colourful logo can't
 * clash with the page) and painted in the page's own text colour, at an
 * opacity solved per page so it reads as the same faint step on a white page,
 * a navy one or a black one. One still image, no blur and no motion, so it
 * costs a phone nothing after the first paint.
 *
 * Everything here is pure so it can be tested without a browser; the canvas
 * work that feeds it lives in components/public/v2/LogoWatermark.tsx.
 */

// The effect list and its save schema live with the other effects; kept
// importable from here for the callers that already use this path.
export { BACKGROUND_EFFECTS, backgroundEffectSchema } from '@/lib/public/effects/catalogue';
export { lightness } from '@/lib/public/effects/strength';

// ── Opacity ─────────────────────────────────────────────────────────────────

/** How far the watermark sits from the page, in L* steps. Faint, but there. */
export const WATERMARK_LIGHTNESS_STEP = 5;
/** Never fainter than this (it would vanish) or stronger (it would fight the text). */
export const WATERMARK_MIN_OPACITY = 0.03;
export const WATERMARK_MAX_OPACITY = 0.16;

/**
 * The opacity at which `tint` over `page` lands `step` L* away from the page.
 *
 * Solving in perceptual lightness rather than picking one opacity is what
 * makes it contrast-aware: white at 6% is a whisper on navy, while near-black
 * at 6% on white is the same whisper, and a mid-tone page needs more. Returns
 * 0 when the tint can't be told apart from the page at all (nothing to draw).
 */
export function watermarkOpacity(page: string, tint: string, step = WATERMARK_LIGHTNESS_STEP): number {
  return solveOpacity(page, tint, step, WATERMARK_MIN_OPACITY, WATERMARK_MAX_OPACITY);
}

/**
 * Centred, the mark sits right behind the headings and ticket rows rather
 * than beside them, so it draws at this share of the solved opacity.
 */
export const CENTRE_OPACITY_FACTOR = 0.62;

// ── Silhouette ──────────────────────────────────────────────────────────────

export interface LogoMask {
  /** One alpha byte per pixel of the crop, 0 to 255, strongest pixel at 255. */
  alpha: Uint8ClampedArray;
  width: number;
  height: number;
  /** How much of the crop the mark fills, 0 to 1. */
  coverage: number;
}

const smoothstep = (e0: number, e1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

/** RGB distance, 0 (same) to 1 (black against white). */
function distance(data: ArrayLike<number>, i: number, ref: [number, number, number]): number {
  const dr = data[i] - ref[0];
  const dg = data[i + 1] - ref[1];
  const db = data[i + 2] - ref[2];
  return Math.sqrt(dr * dr + dg * dg + db * db) / 441.673;
}

/** The most common colour among the given pixel offsets, quantised to 32 steps a channel. */
function dominantColour(data: ArrayLike<number>, offsets: Iterable<number>): [number, number, number] | null {
  const buckets = new Map<number, { n: number; r: number; g: number; b: number }>();
  for (const i of offsets) {
    const key = ((data[i] >> 3) << 10) | ((data[i + 1] >> 3) << 5) | (data[i + 2] >> 3);
    const bucket = buckets.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
    bucket.n++;
    bucket.r += data[i];
    bucket.g += data[i + 1];
    bucket.b += data[i + 2];
    buckets.set(key, bucket);
  }
  let best: { n: number; r: number; g: number; b: number } | null = null;
  for (const b of buckets.values()) if (!best || b.n > best.n) best = b;
  return best ? [best.r / best.n, best.g / best.n, best.b / best.n] : null;
}

/**
 * A logo's RGBA pixels reduced to a single-colour silhouette, cropped to the
 * mark itself. Two kinds of logo come in:
 *
 *   - Cut out (PNG or SVG with transparency): the shape is the alpha. Inside
 *     it, the logo's main colour draws at 60% and everything else (text set
 *     in a badge, a second colour) at full, so a badge keeps its lettering
 *     instead of becoming a solid disc.
 *   - On a plate (JPEG, or a PNG with a white box): the plate is the colour
 *     around the edge, and the shape is everything that differs from it, so a
 *     white box never shows as a faint rectangle.
 *
 * Returns null when there is nothing worth drawing: an empty image, or one
 * that is a solid block (a photo used as a logo), which would read as a stain.
 */
export function logoMask(data: ArrayLike<number>, width: number, height: number): LogoMask | null {
  const total = width * height;
  if (!width || !height || data.length < total * 4) return null;

  let seeThrough = 0;
  for (let p = 0; p < total; p++) if (data[p * 4 + 3] < 250) seeThrough++;
  const cutOut = seeThrough / total > 0.04;

  const raw = new Float32Array(total);
  if (cutOut) {
    const solid: number[] = [];
    for (let p = 0; p < total; p++) if (data[p * 4 + 3] >= 128) solid.push(p * 4);
    const main = dominantColour(data, solid);
    for (let p = 0; p < total; p++) {
      const a = data[p * 4 + 3] / 255;
      if (a === 0) continue;
      const lift = main ? Math.min(1, distance(data, p * 4, main) / 0.35) : 1;
      raw[p] = a * (0.6 + 0.4 * lift);
    }
  } else {
    const edge: number[] = [];
    for (let x = 0; x < width; x++) {
      edge.push(x * 4, ((height - 1) * width + x) * 4);
    }
    for (let y = 0; y < height; y++) {
      edge.push(y * width * 4, (y * width + width - 1) * 4);
    }
    const plate = dominantColour(data, edge);
    if (!plate) return null;
    for (let p = 0; p < total; p++) raw[p] = smoothstep(0.08, 0.32, distance(data, p * 4, plate));
  }

  // Crop to the mark, so placement measures the logo and not its margins.
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  let peak = 0;
  let sum = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const v = raw[y * width + x];
      sum += v;
      if (v > peak) peak = v;
      if (v > 0.03) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0 || peak === 0) return null;
  const coverage = sum / peak / total;
  if (coverage < 0.002 || coverage > 0.85) return null;

  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const alpha = new Uint8ClampedArray(w * h);
  let filled = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const v = raw[(y + minY) * width + x + minX] / peak;
      filled += v;
      alpha[y * w + x] = Math.round(v * 255);
    }
  }
  return { alpha, width: w, height: h, coverage: filled / (w * h) };
}

/**
 * A heavy mark (a solid figure, a filled badge) reads stronger than a line
 * wordmark at the same opacity, so it draws fainter: full strength up to a
 * fifth filled, easing to 60% for a mark that is mostly solid.
 */
export function densityFactor(coverage: number): number {
  if (!Number.isFinite(coverage)) return 1;
  return 1 - 0.4 * smoothstep(0.2, 0.65, coverage);
}

/** The size to read a logo at: its own, scaled so the longer side is at most `max`. SVGs without a size get `max` square. */
export function analysisSize(naturalWidth: number, naturalHeight: number, max = 640): { width: number; height: number } {
  if (!naturalWidth || !naturalHeight) return { width: max, height: max };
  const scale = Math.min(1, max / Math.max(naturalWidth, naturalHeight));
  return { width: Math.max(1, Math.round(naturalWidth * scale)), height: Math.max(1, Math.round(naturalHeight * scale)) };
}

/** Wider than this, a logo is a wordmark and runs down the side instead of across it. */
export const WORDMARK_ASPECT = 2.6;

/**
 * Where the one big mark sits, sized by the logo's shape so a badge and a
 * long wordmark both land well on a phone and a desktop:
 *
 *   - A badge or stacked logo sits off to the right, centred down the page and
 *     bleeding past the edge.
 *   - A wordmark runs top to bottom down the right edge (laid across, it would
 *     be a thin stripe hidden behind the content). On a desktop that puts it
 *     in the margin beside the page; on a phone, a narrow band at the edge.
 *
 * The box is the logo's own (unrotated) shape; `transform` places it.
 */
export function sideMarkBox(aspect: number): { width: string; aspectRatio: string; transform: string } {
  const a = Number.isFinite(aspect) && aspect > 0 ? Math.min(8, Math.max(0.25, aspect)) : 1;
  const aspectRatio = `${a.toFixed(4)} / 1`;
  if (a > WORDMARK_ASPECT) {
    // Turned a quarter, the box's height becomes its width on screen: shift
    // it so that edge sits just inside the right of the page.
    return {
      width: `min(82vh, 900px, ${(30 * a).toFixed(2)}vw)`,
      aspectRatio,
      transform: `translate(calc(50% - ${(50 / a).toFixed(3)}% - 1.5vw), -50%) rotate(90deg)`,
    };
  }
  return {
    width: `min(60vw, 680px, ${(72 * a).toFixed(2)}vh)`,
    aspectRatio,
    transform: 'translate(14%, -50%)',
  };
}

export interface PlacedMarkBox {
  /** The box's horizontal anchor: one of left or right is set. */
  left?: string;
  right?: string;
  width: string;
  aspectRatio: string;
  transform: string;
  /** Multiplies the solved opacity (centred marks draw fainter). */
  opacityFactor: number;
}

/**
 * Where the mark sits for each placement. Right is sideMarkBox, as every page
 * had it. Left is its mirror image (a wordmark turned the other way, so it
 * still reads bottom to top from the page edge). Centre sits the mark in the
 * middle, upright (a wordmark runs across rather than down), smaller, and
 * fainter, since it is behind the content column.
 */
export function placedMarkBox(aspect: number, placement: WatermarkPlacement = 'right'): PlacedMarkBox {
  const a = Number.isFinite(aspect) && aspect > 0 ? Math.min(8, Math.max(0.25, aspect)) : 1;
  if (placement === 'centre') {
    return {
      left: '50%',
      width: a > WORDMARK_ASPECT ? 'min(88vw, 1000px)' : `min(72vw, 620px, ${(60 * a).toFixed(2)}vh)`,
      aspectRatio: `${a.toFixed(4)} / 1`,
      transform: 'translate(-50%, -50%)',
      opacityFactor: CENTRE_OPACITY_FACTOR,
    };
  }
  const side = sideMarkBox(a);
  if (placement === 'right') return { right: '0', ...side, opacityFactor: 1 };
  const transform = a > WORDMARK_ASPECT
    ? `translate(calc(-50% + ${(50 / a).toFixed(3)}% + 1.5vw), -50%) rotate(-90deg)`
    : 'translate(-14%, -50%)';
  return { left: '0', width: side.width, aspectRatio: side.aspectRatio, transform, opacityFactor: 1 };
}
